import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import useAuth from "../../../../hooks/useAuth";
import { apiVariable } from "../../../../api/apiVariable";
import GenericFormPopup from "../../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import { updateCandidateFieldConfiguration } from "../../../../formConfigs/HireCRM/candidateFieldConfig";
import { Candidate } from "../types/CandidateBoardTypes";

interface Props {
  candidate: Candidate;
  onUpdated?: (candidate: Candidate) => void;
}

const UpdateCandidatePopup = ({ candidate, onUpdated }: Props) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { token } = useAuth();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/candidate`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: (response) => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
      if (onUpdated) {
        onUpdated(response.data);
      }
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update candidate");
    },
  });

  const submitHandler = (data: any) => {
    mutation.mutate({ ...data, id: candidate.id });
  };

  const initialValues = {
    id: candidate.id,
    firstName: candidate.firstName,
    lastName: candidate.lastName,
    phoneNumber: candidate.phoneNumber,
    position: candidate.position,
    email: candidate.email,
    status: candidate.status,
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update Candidate
      </Button>
      <GenericFormPopup
        initialValues={initialValues}
        formFields={updateCandidateFieldConfiguration}
        title="Update Candidate"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Candidate updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateCandidatePopup;
